import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Theme, THEMES } from '../lib/types';

interface ThemeContextValue {
    theme: Theme;
    setTheme: (themeId: string) => void;
    availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setActiveTheme] = useState<Theme>(THEMES[0]);

    useEffect(() => {
        // Load saved theme from local storage
        const savedThemeId = localStorage.getItem('intpcat_theme');
        if (savedThemeId) {
            const savedTheme = THEMES.find((t) => t.id === savedThemeId);
            if (savedTheme) {
                setActiveTheme(savedTheme);
            }
        }
    }, []);

    useEffect(() => {
        // Apply theme to document root
        const root = document.documentElement;
        root.style.setProperty('--color-primary', theme.primaryColor);
        root.style.setProperty('--color-secondary', theme.secondaryColor);
        root.style.setProperty('--color-background', theme.backgroundColor);

        // Save to local storage
        localStorage.setItem('intpcat_theme', theme.id);
    }, [theme]);

    const setTheme = (themeId: string) => {
        const newTheme = THEMES.find((t) => t.id === themeId);
        if (newTheme) {
            setActiveTheme(newTheme);
        }
    };

    const value = {
        theme,
        setTheme,
        availableThemes: THEMES,
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
